// External
import { useCallback } from 'react';
import { useStoreActions, useStoreState } from '@/store';

// Internal
import { queryCheckoutSession } from '@/lib/graphql';
import { features } from '@/lib/features';
import { logger } from '@/lib/logger';
import { noop } from '@/lib/utils/miscUtils';
import { getToken } from '@/lib/globals';
import {
  getShippingAddress,
  getShippingCountry,
  mapAddressValues,
} from '@/lib/utils/shipping';
import {
  SERVER_SIDE_SHIPPING_CALLBACK_ERROR_MAPPING,
  SHIPPING_CALLBACK_ERROR_MAPPING,
} from '@/lib/utils/shipping/constants';
import { logOnShippingAddressChangeReceivedToFPTI } from '@/lib/utils/shipping/fpti';
import { conditionallyFetchConversionData } from '@/lib/graphql/operations/currencyConversion';
import { useShippingAddressChangeCallback } from './v2/useShippingAddressChangeCallback';
import { useServerSideShippingAddressChangeCallback } from './useServerSideShippingAddressChangeCallback';
import type {
  CheckoutSessionQueryQuery,
  UpdateGuestShippingAddressCallbackMutationMutation,
} from '@/generated/graphql';
import type {
  EmptyObject,
  MappedAddressValues,
  OnShippingChangeWrapper,
} from '@/lib/utils/shipping/types';
import type { Noop } from '@/types';

type CheckoutSessionCart =
  | UpdateGuestShippingAddressCallbackMutationMutation['updateGuestShippingAddressCallback']
  | CheckoutSessionQueryQuery['checkoutSession'];

/**
 * @NOTE
 * This is the hook that will be utilized by other components such as `Address` in conjunction with
 * `useShippingAddressChangeCallback` and `useServerSideShippingAddressChangeCallback` which are
 * V2 client callbacks and server-side callback respectively.
 *
 * {@link https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=853380692}
 */

export const useShippingAddressChange = (): OnShippingChangeWrapper | Noop => {
  const cartId = getToken();

  const shippingMethods = useStoreState(
    ({ api }) => api.shippingMethods,
  ).filter(s => Boolean(s));

  const cartDetails = useStoreState(({ api }) => api.cartDetails);
  const { amounts } = cartDetails;

  const setCartData = useStoreActions(({ api }) => api.setCartData);
  const setPageLevelError = useStoreActions(({ ui }) => ui.setPageLevelError);
  const setShowLoadingSpinnerWithMessage = useStoreActions(
    ({ ui }) => ui.setShowLoadingSpinnerWithMessage,
  );
  const isShippingCallbacksV2Eligible = features.isActive(
    'isShippingCallbacksV2Eligible',
  );

  const isServerSideShippingCallbacksEligible = features.isActive(
    'isServerSideShippingCallbacksEligible',
  );

  const shippingMethodsForCallback = shippingMethods.filter(
    (s): s is NonNullable<typeof s> => s !== null,
  );

  const onShippingAddressChangeV2ClientCallback =
    useShippingAddressChangeCallback(
      cartId,
      shippingMethodsForCallback,
      amounts,
    );
  const onShippingAddressChangeServerCallback =
    useServerSideShippingAddressChangeCallback();

  // Helper: Handle known and unknown errors
  const handleShippingAddressChangeError = (err: string | Error) => {
    const errMsg = typeof err === 'string' ? err : err?.message;
    let errorLookup;
    if (isServerSideShippingCallbacksEligible) {
      errorLookup =
        SERVER_SIDE_SHIPPING_CALLBACK_ERROR_MAPPING[errMsg] ??
        SERVER_SIDE_SHIPPING_CALLBACK_ERROR_MAPPING.DEFAULT;
    } else {
      errorLookup =
        SHIPPING_CALLBACK_ERROR_MAPPING[errMsg] ??
        SHIPPING_CALLBACK_ERROR_MAPPING.DEFAULT;
    }
    logOnShippingAddressChangeReceivedToFPTI({
      errorDescription: errorLookup,
      status: 'failure',
    });
    setPageLevelError(errorLookup);
    const errorEventName = `${
      isServerSideShippingCallbacksEligible ? 'sssc' : 'cssc'
    }_shipping_address_change_coordinator_execution_error`;
    logger.error(errorEventName, {
      cartId,
      errorDescription: errorLookup,
      isServerSide: isServerSideShippingCallbacksEligible,
      originalError: errMsg,
    });
    window.scrollTo(0, 0);
  };

  // Helper: Update cart state from checkout session data
  const updateCartFromSession = (checkoutSessionData: CheckoutSessionCart) => {
    const cart = checkoutSessionData?.cart;

    if (!checkoutSessionData || !cart) {
      logger.info('did_not_update_cart_from_session', {
        cart,
        checkoutSessionData,
      });
      return;
    }

    const { amounts, items, shippingMethods } = cart;

    logger.info('items_length_in_update_cart_from_session', {
      itemsLength: items?.length,
    });
    setCartData({
      cartAmount: amounts?.total,
      cartDetails: {
        ...cartDetails,
        amounts: amounts ?? cartDetails.amounts,
        items:
          items?.filter(
            (item): item is NonNullable<typeof item> => item !== null,
          ) ?? cartDetails.items,
      },
      shippingMethods: shippingMethods ?? [],
    });
  };

  type FormattedShippingAddress = MappedAddressValues | EmptyObject;

  // Helper: Server-side flow
  const handleServerSideChange = async (
    formattedShippingAddress: FormattedShippingAddress,
  ) => {
    const hasAddress = Object.keys(formattedShippingAddress).length > 0;
    const hasCountry = formattedShippingAddress.country;
    const flowStartEventName = `sssc_shipping_address_change_coordinator_flow_start_${
      hasAddress ? 'has_address' : 'no_address'
    }_${hasCountry ? 'has_country' : 'no_country'}`;

    logger.info(flowStartEventName, {
      addressCountry: formattedShippingAddress.country,
      cartId,
      hasAddress: hasAddress,
      hasCountry: hasCountry,
    });

    const result = await onShippingAddressChangeServerCallback(
      formattedShippingAddress,
      setShowLoadingSpinnerWithMessage,
    );
    if (result.didCallbackFire) {
      await conditionallyFetchConversionData(null, true);
      logOnShippingAddressChangeReceivedToFPTI(
        {
          status: 'success',
        },
        true,
      );
      logger.info('sssc_shipping_address_change_coordinator_flow_success', {
        cartId,
      });
    } else {
      logger.info('sssc_shipping_address_change_coordinator_flow_skipped', {
        cartId,
      });
    }
    return result;
  };

  // Helper: Client-side flow
  const handleClientSideChange = async (
    formattedShippingAddress: FormattedShippingAddress,
  ) => {
    const hasAddress = Object.keys(formattedShippingAddress).length > 0;
    const hasCountry = formattedShippingAddress.country;
    const flowStartEventName = `cssc_shipping_address_change_coordinator_flow_start_${
      hasAddress ? 'has_address' : 'no_address'
    }_${formattedShippingAddress.country ? 'has_country' : 'no_country'}`;

    logger.info(flowStartEventName, {
      addressCountry: formattedShippingAddress.country,
      cartId,
      hasAddress: hasAddress,
      hasCountry: hasCountry,
    });

    // @ts-expect-error - Intentionally call null function to match original behavior
    const didCallbackFire = await onShippingAddressChangeV2ClientCallback(
      formattedShippingAddress,
      setShowLoadingSpinnerWithMessage,
    );
    if (!didCallbackFire) {
      logger.info('cssc_shipping_address_change_coordinator_flow_skipped', {
        cartId,
      });
      return { didCallbackFire: false };
    }
    const [checkoutSessionData] = await Promise.all([
      queryCheckoutSession(),
      conditionallyFetchConversionData(null, true),
    ]);
    logOnShippingAddressChangeReceivedToFPTI({ status: 'success' });
    logger.info('cssc_shipping_address_change_coordinator_flow_success', {
      cartId,
      hasCheckoutSessionData: !!checkoutSessionData,
    });
    return { checkoutSessionData, didCallbackFire: true };
  };

  const execute = useCallback(async () => {
    const eventPrefix = isServerSideShippingCallbacksEligible ? 'sssc' : 'cssc';
    logger.info(
      `${eventPrefix}_shipping_address_change_coordinator_execute_start`,
      {
        cartId,
        isServerSide: isServerSideShippingCallbacksEligible,
      },
    );

    try {
      const shippingAddress = {
        ...getShippingAddress(),
        country: { value: getShippingCountry() },
      };
      const formattedShippingAddress = mapAddressValues(shippingAddress);

      let result;
      if (isServerSideShippingCallbacksEligible) {
        result = await handleServerSideChange(formattedShippingAddress);
      } else {
        result = await handleClientSideChange(formattedShippingAddress);
      }

      if (!result.didCallbackFire) {
        logger.info(
          `${eventPrefix}_shipping_address_change_coordinator_no_callback_fired`,
          {
            cartId,
          },
        );
        return;
      }

      if (result.checkoutSessionData) {
        updateCartFromSession(result.checkoutSessionData);
      }
      setPageLevelError('');
      setShowLoadingSpinnerWithMessage(null);
      logOnShippingAddressChangeReceivedToFPTI({ status: 'success' });

      const completionEventName = `${
        isServerSideShippingCallbacksEligible ? 'sssc' : 'cssc'
      }_shipping_address_change_coordinator_execute_complete`;
      logger.info(completionEventName, {
        cartId,
        isServerSide: isServerSideShippingCallbacksEligible,
      });
    } catch (err: any) {
      handleShippingAddressChangeError(err);
    }
  }, [
    onShippingAddressChangeV2ClientCallback,
    onShippingAddressChangeServerCallback,
    cartDetails,
    setCartData,
    setPageLevelError,
    isServerSideShippingCallbacksEligible,
    isShippingCallbacksV2Eligible,
    setShowLoadingSpinnerWithMessage,
  ]);

  if (
    !isShippingCallbacksV2Eligible &&
    !isServerSideShippingCallbacksEligible
  ) {
    logger.info('use_shipping_address_change_returned_noop');
    return noop;
  }

  return execute;
};
